/**
 * OnboardingPage – 3-step flow captured on one screen for MVP simplicity.
 *   Step 1: choose direction (Practice Spanish / Practice English)
 *   Step 2: self-rate comfort (1..5)
 *   Step 3: baseline voice check (optional) – 15s mic test
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CaretRight, CheckCircle, Microphone } from '@phosphor-icons/react';
import { http, apiErrorMessage } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import useRecorder from '@/hooks/useRecorder';

export default function OnboardingPage() {
  const { user, patchUser } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(user?.practice_direction || 'en_to_es');
  const [comfort, setComfort] = useState(user?.comfort_level || 2);
  // Newcomer Mode — added as Step 2 ("First time?"). If yes we route to the
  // flashcard deck after onboarding instead of Home Free-Talk.
  const [isFirstTime, setIsFirstTime] = useState(null); // null | true | false
  const [baselineHeard, setBaselineHeard] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const recorder = useRecorder();

  async function doBaseline() {
    try {
      await recorder.start();
      setTimeout(async () => {
        const blob = await recorder.stop();
        if (!blob) return;
        // We don't need the transcript for onboarding — just confirm mic works.
        setBaselineHeard(true);
      }, 5000);
    } catch (e) {
      setError(e.message || 'Microphone permission denied');
    }
  }

  async function finish() {
    setSaving(true);
    setError('');
    try {
      // Newcomer-aware onboarding endpoint. The legacy /onboarding still
      // works; this one additionally captures `is_first_time` and flips
      // level_kind="newcomer" on the user when true.
      const { data } = await http.post('/onboarding/first-time', {
        practice_direction: direction,
        comfort_level: comfort,
        is_first_time: !!isFirstTime,
        baseline_heard: baselineHeard,
      });
      patchUser(data);
      // True beginners go straight to the flashcard deck instead of Home.
      navigate(isFirstTime ? '/newcomer' : '/home', { replace: true });
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="lf-shell flex flex-col px-6 py-10" data-testid="onboarding-page">
      <div className="mb-8 flex items-center gap-2 text-sm text-[var(--lf-text-muted)]" data-testid="onboarding-step-indicator">
        <Dot active={step >= 1} />
        <Dot active={step >= 2} />
        <Dot active={step >= 3} />
        <Dot active={step >= 4} />
        <span className="ml-auto">Step {step} of 4</span>
      </div>

      {step === 1 && (
        <motion.div key="s1" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <h2 className="text-3xl font-bold">Which language do you want to practice?</h2>
          <p className="mt-2 text-[var(--lf-text-muted)]">You can change this later in settings.</p>

          <div className="mt-6 space-y-3">
            <ChoiceCard
              active={direction === 'en_to_es'}
              onClick={() => setDirection('en_to_es')}
              title="Practice Spanish"
              sub="Your native language is English"
              emoji="🇪🇸"
              testid="direction-es"
            />
            <ChoiceCard
              active={direction === 'es_to_en'}
              onClick={() => setDirection('es_to_en')}
              title="Practica Inglés"
              sub="Tu idioma nativo es el Español"
              emoji="🇬🇧"
              testid="direction-en"
            />
          </div>

          <button onClick={() => setStep(2)} className="lf-btn lf-btn-primary w-full mt-8" data-testid="onboarding-next-1">
            Continue <CaretRight size={18} weight="bold" />
          </button>
        </motion.div>
      )}

      {step === 2 && (
        <motion.div key="s2-newcomer" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <h2 className="text-3xl font-bold">Have you spoken {direction === 'en_to_es' ? 'Spanish' : 'English'} before?</h2>
          <p className="mt-2 text-[var(--lf-text-muted)]">
            If this is brand new, we'll start with 50 friendly flashcards — no pressure to talk yet.
          </p>

          <div className="mt-6 space-y-3">
            <ChoiceCard
              active={isFirstTime === true}
              onClick={() => setIsFirstTime(true)}
              title="No, this is my first time"
              sub="Begin with the Newcomer Pack — pictures, sounds, slow pace."
              emoji="🌱"
              testid="firsttime-yes"
            />
            <ChoiceCard
              active={isFirstTime === false}
              onClick={() => setIsFirstTime(false)}
              title="Yes, I know a little"
              sub="Skip flashcards and jump into conversations."
              emoji="🚀"
              testid="firsttime-no"
            />
          </div>

          <div className="mt-8 flex gap-3">
            <button onClick={() => setStep(1)} className="lf-btn lf-btn-ghost flex-1" data-testid="onboarding-back-2">Back</button>
            <button
              onClick={() => setStep(3)}
              disabled={isFirstTime === null}
              className="lf-btn lf-btn-primary flex-[2]"
              data-testid="onboarding-next-2"
            >
              Continue <CaretRight size={18} weight="bold" />
            </button>
          </div>
        </motion.div>
      )}

      {step === 3 && (
        <motion.div key="s2" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <h2 className="text-3xl font-bold">How comfortable are you speaking right now?</h2>
          <p className="mt-2 text-[var(--lf-text-muted)]">
            No pressure — we'll adapt as we go. Just your honest starting point.
          </p>

          <div className="mt-6 space-y-2">
            {[
              { v: 1, label: 'Just starting out', sub: 'A few words here and there.' },
              { v: 2, label: 'Still nervous', sub: 'Can say simple things slowly.' },
              { v: 3, label: 'Getting there', sub: 'Can hold short conversations.' },
              { v: 4, label: 'Fairly fluent', sub: 'Comfortable most of the time.' },
              { v: 5, label: 'Confident', sub: 'I want a challenge.' },
            ].map((opt) => (
              <ChoiceCard
                key={opt.v}
                active={comfort === opt.v}
                onClick={() => setComfort(opt.v)}
                title={opt.label}
                sub={opt.sub}
                emoji={['🌱','🪴','🌳','✨','🌟'][opt.v - 1]}
                testid={`comfort-${opt.v}`}
              />
            ))}
          </div>

          <div className="mt-8 flex gap-3">
            <button onClick={() => setStep(2)} className="lf-btn lf-btn-ghost flex-1" data-testid="onboarding-back-3">Back</button>
            <button onClick={() => setStep(4)} className="lf-btn lf-btn-primary flex-[2]" data-testid="onboarding-next-3">
              Continue <CaretRight size={18} weight="bold" />
            </button>
          </div>
        </motion.div>
      )}

      {step === 4 && (
        <motion.div key="s3" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <h2 className="text-3xl font-bold">Quick mic check</h2>
          <p className="mt-2 text-[var(--lf-text-muted)]">
            We'll do a 5-second voice test so your first chat feels smooth. This is optional.
          </p>

          <div className="lf-card mt-6 p-6 text-center">
            <div className="grid h-20 w-20 mx-auto place-items-center rounded-full bg-[var(--lf-surface-muted)] text-[var(--lf-primary)]">
              {baselineHeard ? <CheckCircle size={32} weight="fill" /> : <Microphone size={32} weight="duotone" />}
            </div>
            <div className="mt-4 font-semibold">
              {baselineHeard
                ? 'Great — we heard you loud and clear.'
                : recorder.recording
                  ? 'Listening for 5 seconds… say anything!'
                  : "Tap below and say hi in any language."}
            </div>
            {recorder.error ? (
              <div className="mt-2 text-sm text-[var(--lf-destructive)]">{recorder.error}</div>
            ) : null}
            <button
              onClick={doBaseline}
              disabled={recorder.recording || baselineHeard}
              className="lf-btn lf-btn-secondary mt-4"
              data-testid="onboarding-mic-btn"
            >
              {recorder.recording ? 'Listening…' : baselineHeard ? 'Done' : 'Start mic check'}
            </button>
          </div>

          {error ? <div className="mt-4 text-sm text-[var(--lf-destructive)]" data-testid="onboarding-error">{error}</div> : null}

          <div className="mt-8 flex gap-3">
            <button onClick={() => setStep(3)} className="lf-btn lf-btn-ghost flex-1" data-testid="onboarding-back-4">Back</button>
            <button onClick={finish} disabled={saving} className="lf-btn lf-btn-primary flex-[2]" data-testid="onboarding-finish">
              {saving ? 'Saving…' : 'Start practicing'}
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function Dot({ active }) {
  return <span className={`h-2 w-8 rounded-full transition-colors ${active ? 'bg-[var(--lf-primary)]' : 'bg-[var(--lf-border)]'}`} />;
}

function ChoiceCard({ active, onClick, title, sub, emoji, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`lf-card flex w-full items-center gap-3 p-4 text-left transition-all duration-200 ${active ? 'ring-2 ring-[var(--lf-primary)]' : 'hover:-translate-y-0.5'}`}
    >
      <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[var(--lf-surface-muted)] text-2xl">
        {emoji}
      </div>
      <div className="flex-1">
        <div className="font-semibold">{title}</div>
        <div className="text-sm text-[var(--lf-text-muted)]">{sub}</div>
      </div>
      {active ? <CheckCircle size={22} weight="fill" className="text-[var(--lf-primary)]" /> : null}
    </button>
  );
}
